import React from 'react';
import { StepProps } from '@/types/broker-registration.types';
import { FormField } from '../FormField';
import { SelectInput } from '../SelectInput';
import { CheckboxGroup } from '../CheckboxGroup';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  POST_SALE_ROLE_OPTIONS,
  POF_REQUIREMENT_OPTIONS,
  CASH_DOWN_PERCENT_OPTIONS,
  FIXED_AMOUNT_OPTIONS,
  FIXED_AMOUNT_MAX_OPTIONS,
  POF_DOCUMENT_TYPES,
  COMPETING_OFFERS_OPTIONS,
  DEAL_SIZE_OPTIONS,
  FEE_STRUCTURE_OPTIONS,
} from '@/constants/broker-registration.constants';

export function StepFour({ formData, updateFormData, errors, clearError }: StepProps) {
  const { t } = useLanguage();
  const showCashDownOptions = formData.vettingProcess.includes('cash_down_payment');
  const showFixedAmountOptions = formData.vettingProcess.includes('fixed_liquid_amount');

  // Get translated options
  const postSaleRoleOptions = POST_SALE_ROLE_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.post_sale_role.${opt.value}`)
  }));

  const pofRequirementOptions = POF_REQUIREMENT_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.pof_requirements.${opt.value}`)
  }));

  const cashDownPercentOptions = CASH_DOWN_PERCENT_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.cash_down_percent.${opt.value}`)
  }));

  const fixedAmountOptions = FIXED_AMOUNT_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.fixed_amounts.${opt.value}`)
  }));

  const fixedAmountMaxOptions = FIXED_AMOUNT_MAX_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.fixed_amounts.${opt.value}`)
  }));

  const pofDocumentTypes = POF_DOCUMENT_TYPES.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.pof_types.${opt.value}`)
  }));

  const competingOffersOptions = COMPETING_OFFERS_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.competing_offers.${opt.value}`)
  }));

  const dealSizeOptions = DEAL_SIZE_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.deal_sizes.${opt.value}`)
  }));

  const feeStructureOptions = FEE_STRUCTURE_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(`broker_registration.options.fee_structure.${opt.value}`)
  }));

  const togglePOFOption = (value: string) => {
    const newValues = formData.vettingProcess.includes(value)
      ? formData.vettingProcess.filter((v) => v !== value)
      : [...formData.vettingProcess, value];

    // Clear dependent fields when unchecking
    const updates: Partial<typeof formData> = { vettingProcess: newValues };
    if (value === 'cash_down_payment' && !newValues.includes(value)) {
      updates.cashDownMinPercent = '';
    }
    if (value === 'fixed_liquid_amount' && !newValues.includes(value)) {
      updates.fixedAmountMin = '';
      updates.fixedAmountMax = '';
    }

    updateFormData(updates);
    clearError('vettingProcess');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b border-slate-200 pb-3 text-[#1D314D]">
        {t('broker_registration.step_four.title')}
      </h2>
      <p className="text-sm text-slate-600 mb-6">
        {t('broker_registration.step_four.subtitle')}
      </p>

      <FormField
        label={t('broker_registration.step_four.post_sale_role')}
        helperText={t('broker_registration.step_four.post_sale_role_hint')}
        error={errors.postSaleRole}
        required
      >
        <CheckboxGroup
          values={formData.postSaleRole}
          onChange={(values) => {
            updateFormData({ postSaleRole: values });
            clearError('postSaleRole');
          }}
          options={postSaleRoleOptions}
          columns={1}
          error={!!errors.postSaleRole}
        />
      </FormField>

      <div>
        <FormField
          label={t('broker_registration.step_four.vetting_process')}
          error={errors.vettingProcess}
          required
          helperText={t('broker_registration.step_four.vetting_process_hint')}
        >
          <div className="space-y-3">
            {pofRequirementOptions.map((option) => (
              <div key={option.value} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.vettingProcess.includes(option.value)}
                    onChange={() => togglePOFOption(option.value)}
                    className="h-4 w-4 mt-1 text-[#CEAB65] rounded border-slate-300 focus:ring-[#CEAB65]"
                  />
                  <span className="ml-3 text-sm text-[#1D314D]">{option.label}</span>
                </label>

                {/* Conditional sub-options for cash_down_payment */}
                {option.value === 'cash_down_payment' && showCashDownOptions && (
                  <div className="ml-7 mt-3 pl-3 border-l-2 border-[#CEAB65]">
                    <FormField
                      label={t('broker_registration.step_four.cash_down_min_percent')}
                      error={errors.cashDownMinPercent}
                      required
                    >
                      <SelectInput
                        value={formData.cashDownMinPercent}
                        onChange={(value) => {
                          updateFormData({ cashDownMinPercent: value });
                          clearError('cashDownMinPercent');
                        }}
                        options={cashDownPercentOptions}
                        error={!!errors.cashDownMinPercent}
                        className="w-full md:w-1/2"
                      />
                    </FormField>
                  </div>
                )}

                {/* Conditional sub-options for fixed_liquid_amount */}
                {option.value === 'fixed_liquid_amount' && showFixedAmountOptions && (
                  <div className="ml-7 mt-3 pl-3 border-l-2 border-[#CEAB65]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label={t('broker_registration.step_four.fixed_amount_min')}
                        error={errors.fixedAmountMin}
                        required
                      >
                        <SelectInput
                          value={formData.fixedAmountMin}
                          onChange={(value) => {
                            updateFormData({ fixedAmountMin: value });
                            clearError('fixedAmountMin');
                          }}
                          options={fixedAmountOptions}
                          error={!!errors.fixedAmountMin}
                        />
                      </FormField>
                      <FormField
                        label={t('broker_registration.step_four.fixed_amount_max')}
                        error={errors.fixedAmountMax}
                        required
                      >
                        <SelectInput
                          value={formData.fixedAmountMax}
                          onChange={(value) => {
                            updateFormData({ fixedAmountMax: value });
                            clearError('fixedAmountMax');
                          }}
                          options={fixedAmountMaxOptions}
                          error={!!errors.fixedAmountMax}
                        />
                      </FormField>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </FormField>
      </div>

      <FormField
        label={t('broker_registration.step_four.accepted_pof_types')}
        helperText={t('broker_registration.step_four.accepted_pof_types_hint')}
        error={errors.acceptedPofTypes}
        required
      >
        <CheckboxGroup
          values={formData.acceptedPofTypes}
          onChange={(values) => {
            updateFormData({ acceptedPofTypes: values });
            clearError('acceptedPofTypes');
          }}
          options={pofDocumentTypes}
          error={!!errors.acceptedPofTypes}
        />
      </FormField>

      <FormField
        label={t('broker_registration.step_four.competing_offers')}
        helperText={t('broker_registration.step_four.competing_offers_hint')}
        error={errors.competingOffers}
        required
      >
        <CheckboxGroup
          values={formData.competingOffers}
          onChange={(values) => {
            updateFormData({ competingOffers: values });
            clearError('competingOffers');
          }}
          options={competingOffersOptions}
          columns={1}
          error={!!errors.competingOffers}
        />
      </FormField>

      <FormField
        label="Deal Size Range"
        required
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('broker_registration.step_four.deal_size_min')} error={errors.dealSizeMin} required>
            <SelectInput
              value={formData.dealSizeMin}
              onChange={(value) => {
                updateFormData({ dealSizeMin: value });
                clearError('dealSizeMin');
              }}
              options={dealSizeOptions}
              error={!!errors.dealSizeMin}
            />
          </FormField>
          <FormField label={t('broker_registration.step_four.deal_size_max')} error={errors.dealSizeMax} required>
            <SelectInput
              value={formData.dealSizeMax}
              onChange={(value) => {
                updateFormData({ dealSizeMax: value });
                clearError('dealSizeMax');
              }}
              options={dealSizeOptions}
              error={!!errors.dealSizeMax}
            />
          </FormField>
        </div>
      </FormField>

      <FormField
        label={t('broker_registration.step_four.fee_structure')}
        helperText={t('broker_registration.step_four.fee_structure_hint')}
        error={errors.feeStructure}
        required
      >
        <CheckboxGroup
          values={formData.feeStructure}
          onChange={(values) => {
            updateFormData({ feeStructure: values });
            clearError('feeStructure');
          }}
          options={feeStructureOptions}
          error={!!errors.feeStructure}
        />
      </FormField>
    </div>
  );
}
